<template>
  <div class="main-container">
    <p class="title title--main title--main--lg q-py-md q-ml-xs">
      {{ displayTitle }}
      <span class="header-actions">
        <el-tooltip
          v-if="featureFlag('viewer_recenter_camera')"
          class="bg-tooltip"
          effect="dark"
          :content="$t('panelMenu.recenterCamera')"
          placement="bottom"
        >
          <span
            class="recenter-btn"
            :class="{ 'is-flashing': recenterFlashing }"
            @click="onRecenterClick"
          >
            <svg
              class="recenter-icon"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g class="recenter-corners">
                <path d="M3 3 H9 V5 H5 V9 H3 Z" />
                <path d="M15 3 H21 V9 H19 V5 H15 Z" />
                <path d="M3 15 H5 V19 H9 V21 H3 Z" />
                <path d="M19 15 H21 V21 H15 V19 H19 Z" />
              </g>
              <circle class="recenter-dot" cx="12" cy="12" r="2.5" />
            </svg>
          </span>
        </el-tooltip>
        <img
          src="@/assets/icons/close-white.svg"
          class="cursor-pointer"
          @click="closeSidebar"
        />
      </span>
    </p>
    <div class="child-container">
      <div class="q-px-md">
        <p class="title">
          {{ $t("panelMenu.panelType") }}
        </p>
        <div class="divider" />
        <div v-if="panelDetails">
          <p class="title--panel">
            {{ panelDetails.name }}
          </p>
          <div class="row q-mt-xs selected-panel">
            <img class="panel-img" :src="textureUrl" />
            <p>
              {{ Math.trunc(greaterPanelDimension * 1000) }}mm x
              {{ Math.trunc(lowerPanelDimension * 1000) }}mm
            </p>
          </div>
        </div>
        <div class="q-mt-sm">
          <span
            class="select-panel-btn"
            @click="handlePanelSelectionPopup(!panelSelectionOpen)"
          >
            {{ $t("sidebar.changePanel") }}
          </span>
        </div>
      </div>
    </div>
    <div class="child-container">
      <div class="q-px-sm">
        <p class="title">
          {{ $t("panelMenu.layoutSettings") }}
        </p>
        <div class="divider" />

        <!-- Mounting type selector — controlled by feature flag. -->
        <template v-if="featureFlag('solar_mounting_type')">
          <p class="title--panel q-mb-xs">{{ $t("rackMode.mountingType") }}</p>
          <div class="mounting-type-selector q-mb-md">
            <div
              v-for="mode in mountingModes"
              :key="mode.iconMode"
              class="mounting-mode"
              :class="{ 'mounting-mode--active': rackMode === mode.value }"
              @click="rackMode = mode.value"
            >
              <MountingTypeIcon
                :mode="mode.iconMode"
                :color="rackMode === mode.value ? 'var(--color-gree300)' : 'white'"
              />
              <span class="mounting-mode__label">{{ $t(mode.labelKey) }}</span>
            </div>
          </div>
        </template>

        <!-- Panel orientation (always visible) -->
        <p class="title--panel">{{ $t("panelMenu.panelOrientation") }}</p>
        <div class="q-mb-sm">
          <q-radio v-model="orientation" :val="true"  :label="$t('panelMenu.vertical')"   color="gree" size="sm" class="q-mr-sm" label-color="white" dark dense />
          <q-radio v-model="orientation" :val="false" :label="$t('panelMenu.horizontal')" color="gree" label-color="white" size="sm" dark />
        </div>

        <!-- ── FLUSH fields ── -->
        <template v-if="rackMode === null">
          <div v-if="featureFlag('solar_row_alignment')" class="q-mb-md">
            <p class="title--panel q-mb-xs">
              {{ $t("panelMenu.rowAlignment") }}
              <span>
                <el-tooltip
                  class="bg-tooltip"
                  effect="dark"
                  :content="$t('panelMenu.rowAlignmentInfo')"
                  placement="bottom"
                >
                  <img
                    src="@/assets/icons/info-icon.svg"
                    class="q-ml-xs q-mb-xxs cursor-pointer"
                  />
                </el-tooltip>
              </span>
            </p>
            <div class="alignment-grid">
              <div
                v-for="option in [
                  { value: 0, label: '0%' },
                  { value: 33.3, label: '33%' },
                  { value: 50, label: '50%' },
                  { value: 66.6, label: '66%' },
                ]"
                :key="option.value"
                class="alignment-card"
                :class="{
                  'alignment-card--active':
                    Math.floor(rowOverlap) === Math.floor(option.value),
                }"
                @click="rowOverlap = option.value"
              >
                <GridAlignmentIcon
                  :value="option.value"
                  :color="
                    Math.floor(rowOverlap) === Math.floor(option.value)
                      ? 'var(--color-gree300)'
                      : 'white'
                  "
                />
                <span
                  class="alignment-card__label"
                  :style="{
                    color:
                      Math.floor(rowOverlap) === Math.floor(option.value)
                        ? 'var(--color-gree300)'
                        : 'white',
                  }"
                  >{{ option.label }}</span
                >
              </div>
            </div>
            <div class="custom-alignment-row">
              <span class="title--panel">{{ $t("panelMenu.customAlignment") }}</span>
              <el-input-number v-model="rowOverlap" :min="0" :max="99" :precision="1" :formatter="(v) => Math.floor(v)" :value-on-clear="0" controls-position="right" class="custom-alignment-input">
                <template #increase-icon><img src="@/assets/icons/arrow-up.svg" /></template>
                <template #decrease-icon><img src="@/assets/icons/arrow-down.svg" /></template>
              </el-input-number>
            </div>
          </div>
          <GridInputGroup v-model="verticalSpacing"   :title="$t('panelMenu.rowsSpacing')"    :tooltipContent="$t('panelMenu.rowsSpacingInfo')"    iconSrc="/assets/icons/rows-spacing.svg" />
          <GridInputGroup v-model="horizontalSpacing" :title="$t('panelMenu.columnsSpacing')" :tooltipContent="$t('panelMenu.columnsSpacingInfo')" iconSrc="/assets/icons/columns-spacing.svg" />
          <GridInputGroup v-model="margin"            :title="$t('panelMenu.margin')"         :tooltipContent="$t('panelMenu.marginInfo')"         iconSrc="/assets/icons/margin.svg" />
          <GridInputGroup v-model="offset"            :title="$t('panelMenu.offset')"         :tooltipContent="$t('panelMenu.offsetInfo')"         iconSrc="/assets/icons/offset.svg" />
        </template>

        <!-- ── EAST-WEST fields ── -->
        <template v-else-if="rackMode === 'EAST_WEST'">
          <GridInputGroup v-model="tilt"    :title="$t('tiltedMode.tilt')"    :tooltipContent="$t('tiltedMode.tiltInfo')"    :iconSrc="icons.tiltAngle"    unit="°" :max="90" />

          <!-- Azimuth with Reset -->
          <div class="q-mb-md">
            <p class="title--panel q-mb-xs">
              {{ $t("tiltedMode.azimuth") }}
              <span>
                <el-tooltip class="bg-tooltip" effect="dark" :content="$t('tiltedMode.azimuthInfoEastWest')" placement="bottom" raw-content>
                  <img src="@/assets/icons/info-icon.svg" class="q-ml-xs q-mb-xxs cursor-pointer" />
                </el-tooltip>
              </span>
            </p>
            <div class="row items-center no-wrap" style="gap:8px">
              <img :src="icons.azimuth" width="24" style="margin: 0 3px;" />
              <el-input-number v-model="azimuth" :min="0" :max="360" :precision="0" :value-on-clear="0" controls-position="right" class="tilted-input" style="--unit-label: '°'">
                <template #increase-icon><img src="@/assets/icons/arrow-up.svg" /></template>
                <template #decrease-icon><img src="@/assets/icons/arrow-down.svg" /></template>
              </el-input-number>
              <el-tooltip class="bg-tooltip" effect="dark" :content="$t('tiltedMode.reset')" placement="bottom">
                <img :src="icons.reset" class="reset-btn cursor-pointer" @click="resetAzimuth" />
              </el-tooltip>
            </div>
          </div>
          <GridInputGroup v-model="pairGap" :title="$t('tiltedMode.pairGap')" :tooltipContent="$t('tiltedMode.pairGapInfo')" :iconSrc="icons.pairGap"       :min="pairGapMin" :max="999" :precision="1" unitRight="50px" />

          <!-- A2A — editable with Reset -->
          <div class="q-mb-md">
            <p class="title--panel q-mb-xs">
              {{ $t("tiltedMode.apexToApex") }}
              <span>
                <el-tooltip class="bg-tooltip" effect="dark" :content="$t('tiltedMode.apexToApexInfo')" placement="bottom" raw-content>
                  <img src="@/assets/icons/info-icon.svg" class="q-ml-xs q-mb-xxs cursor-pointer" />
                </el-tooltip>
              </span>
            </p>
            <div class="row items-center no-wrap" style="gap:8px">
              <img :src="icons.a2a" width="30" />
              <el-input-number v-model="apexToApexDisplay" :min="apexToApexMin" :max="9999" :precision="1" :value-on-clear="apexToApexMin" controls-position="right" class="tilted-input" style="--unit-label: 'cm'; --unit-right: 50px">
                <template #increase-icon><img src="@/assets/icons/arrow-up.svg" /></template>
                <template #decrease-icon><img src="@/assets/icons/arrow-down.svg" /></template>
              </el-input-number>
              <el-tooltip class="bg-tooltip" effect="dark" :content="$t('tiltedMode.reset')" placement="bottom">
                <img :src="icons.reset" class="reset-btn cursor-pointer" @click="resetApexToApex" />
              </el-tooltip>
            </div>

            <!-- Inter-gap — nested clearance distance, two-way linked to A2A -->
            <div class="inter-gap-row q-mt-sm">
              <p class="title--panel q-mb-xs">
                {{ $t("tiltedMode.interGap") }}
                <span>
                  <el-tooltip class="bg-tooltip" effect="dark" :content="$t('tiltedMode.interGapInfo')" placement="bottom" raw-content>
                    <img src="@/assets/icons/info-icon.svg" class="q-ml-xs q-mb-xxs cursor-pointer" />
                  </el-tooltip>
                </span>
              </p>
              <div class="row items-center no-wrap" style="gap:8px">
                <img :src="icons.interRow" width="30" />
                <el-input-number v-model="interGap" :min="0" :max="9999" :precision="1" :value-on-clear="0" controls-position="right" class="tilted-input" style="--unit-label: 'cm'; --unit-right: 50px">
                  <template #increase-icon><img src="@/assets/icons/arrow-up.svg" /></template>
                  <template #decrease-icon><img src="@/assets/icons/arrow-down.svg" /></template>
                </el-input-number>
              </div>
            </div>
          </div>

          <GridInputGroup v-model="margin" :title="$t('panelMenu.margin')" :tooltipContent="$t('panelMenu.marginInfo')" iconSrc="/assets/icons/margin.svg" />
          <GridInputGroup v-model="offset" :title="$t('panelMenu.offset')" :tooltipContent="$t('panelMenu.offsetInfo')" iconSrc="/assets/icons/offset.svg" />
        </template>

        <!-- ── FIXED-TILT fields ── -->
        <template v-else-if="rackMode === 'FIXED_TILT'">
          <GridInputGroup v-model="tilt"    :title="$t('tiltedMode.tilt')"    :tooltipContent="$t('tiltedMode.tiltInfo')"    :iconSrc="icons.tiltAngle"    unit="°" :max="90" />

          <!-- Azimuth with Reset -->
          <div class="q-mb-md">
            <p class="title--panel q-mb-xs">
              {{ $t("tiltedMode.azimuth") }}
              <span>
                <el-tooltip class="bg-tooltip" effect="dark" :content="$t('tiltedMode.azimuthInfoFixedTilt')" placement="bottom" raw-content>
                  <img src="@/assets/icons/info-icon.svg" class="q-ml-xs q-mb-xxs cursor-pointer" />
                </el-tooltip>
              </span>
            </p>
            <div class="row items-center no-wrap" style="gap:8px">
              <img :src="icons.azimuth" width="24" style="margin: 0 3px;" />
              <el-input-number v-model="azimuth" :min="0" :max="360" :precision="0" :value-on-clear="0" controls-position="right" class="tilted-input" style="--unit-label: '°'">
                <template #increase-icon><img src="@/assets/icons/arrow-up.svg" /></template>
                <template #decrease-icon><img src="@/assets/icons/arrow-down.svg" /></template>
              </el-input-number>
              <el-tooltip class="bg-tooltip" effect="dark" :content="$t('tiltedMode.reset')" placement="bottom">
                <img :src="icons.reset" class="reset-btn cursor-pointer" @click="resetAzimuth" />
              </el-tooltip>
            </div>
          </div>

          <!-- Inter-row spacing with Reset -->
          <div class="q-mb-md">
            <p class="title--panel q-mb-xs">
              {{ $t("tiltedMode.interRowSpacing") }}
              <span>
                <el-tooltip class="bg-tooltip" effect="dark" :content="$t('tiltedMode.interRowSpacingInfo')" placement="bottom" raw-content>
                  <img src="@/assets/icons/info-icon.svg" class="q-ml-xs q-mb-xxs cursor-pointer" />
                </el-tooltip>
              </span>
            </p>
            <div class="row items-center no-wrap" style="gap:8px">
              <img :src="icons.interRowSpacing" width="30" />
              <el-input-number v-model="interRowSpacingDisplay" :min="0" :max="9999" :precision="1" :value-on-clear="0" controls-position="right" class="tilted-input" style="--unit-label: 'cm'; --unit-right: 50px">
                <template #increase-icon><img src="@/assets/icons/arrow-up.svg" /></template>
                <template #decrease-icon><img src="@/assets/icons/arrow-down.svg" /></template>
              </el-input-number>
              <el-tooltip class="bg-tooltip" effect="dark" :content="$t('tiltedMode.reset')" placement="bottom">
                <img :src="icons.reset" class="reset-btn cursor-pointer" @click="resetInterRowSpacing" />
              </el-tooltip>
            </div>
          </div>

          <GridInputGroup v-model="margin" :title="$t('panelMenu.margin')" :tooltipContent="$t('panelMenu.marginInfo')" iconSrc="/assets/icons/margin.svg" />
          <GridInputGroup v-model="offset" :title="$t('panelMenu.offset')" :tooltipContent="$t('panelMenu.offsetInfo')" iconSrc="/assets/icons/offset.svg" />
        </template>
      </div>
    </div>
    <RestrictedArea
      class="child-container"
      :active="active"
      :handleCreateRestrictedArea="handleCreateRestrictedArea"
      :disableRestrictedAreaMode="disableRestrictedAreaMode"
    />
    <IndividualPlacement
      v-if="rackMode === null"
      class="child-container child-container--last"
      :individualPanelOrientation="individualPanelOrientation"
      :enableIndividualPlacementMode="enableIndividualPlacementMode"
      :disableIndividualPlacementMode="disableIndividualPlacementMode"
    />
    <div class="child-container--remove q-ml-xs">
      <span class="title title--red cursor-pointer" @click="removeGroupModalOpen = true">
        {{ getDeleteText }}
      </span>
    </div>
  </div>

  <PanelSelection
    v-if="panelSelectionOpen"
    :handlePanelSelectionPopup="handlePanelSelectionPopup"
    :panelOrientation="orientation"
    :likedPanelProp="likedPanel"
    :horizontalPanels="horizontalPanels"
    :verticalPanels="verticalPanels"
    :activeUserId="activeUserId"
    :anonymousUser="anonymousUser"
    :starClicked="starClicked"
    :unlikePanel="unlikePanel"
    :likedPanelId="likedPanelId"
    :selectedPanelId="this.selectedSolarGroup.panelId"
    :loadCustomPanelTextures="loadCustomPanelTextures"
    :getUserDefaultSolarValues="getUserDefaultSolarValues"
    :individualPanelsExist="individualPanelsExist"
    @panelSelected="handleChangeSelectPanel"
    @toggleHotkeys="$emit('toggleHotkeys', $event)"
  />

  <RemoveSolarGroupModal
    :isVisible="removeGroupModalOpen"
    :areaName="isBobbieContext ? null : displayTitle"
    :areaIndex="isBobbieContext ? solarGroupIndex : null"
    @confirm="handleDeleteSolarGroup"
    @close-modal="closeRemoveGroupModal"
    @update:isVisible="removeGroupModalOpen = $event"
  />

  <RemoveSolarGroupModal
    :isVisible="removeGroupModalOpen"
    :areaName="isBobbieContext ? null : displayTitle"
    :areaIndex="isBobbieContext ? solarGroupIndex : null"
    @confirm="handleDeleteSolarGroup"
    @close-modal="closeRemoveGroupModal"
    @update:isVisible="removeGroupModalOpen = $event"
  />
</template>

<script>
import PanelSelection from "@/components/RightSidebar/PanelSelection.vue";
import RestrictedArea from "@/components/RightSidebar/RestrictedArea.vue";
import IndividualPlacement from "@/components/RightSidebar/IndividualPlacement.vue";
import API from "@/api/API.js";
import { hasPermission, Permission } from "@/constants/permissions";
import { mapGetters } from "vuex";
import rowsSpacingIcon from "@/assets/icons/rows-spacing.svg";
import columnsSpacingIcon from "@/assets/icons/columns-spacing.svg";
import marginIcon from "@/assets/icons/margin.svg";
import offsetIcon from "@/assets/icons/offset.svg";
import GridInputGroup from "./GridInputGroup.vue";
import GridAlignmentIcon from "./GridAlignmentIcon.vue";
import MountingTypeIcon from "./MountingTypeIcon.vue";
import tiltAngleIcon from "@/assets/icons/tilt-angle.svg";
import azimuthIcon from "@/assets/icons/azimuth.svg";
import interRowSpacingIcon from "@/assets/icons/inter-row-spacing.svg";
import interRowIcon from "@/assets/icons/inter-row.svg";
import pairGapIcon from "@/assets/icons/pair-gap.svg";
import a2aIcon from "@/assets/icons/a2a.svg";
import resetIcon from "@/assets/icons/reset.svg";
import { computeDefaultAzimuth, computeInterRowSpacing } from "@/modules/fixedTilt.js";
import {
  FIXED_TILT,
  EAST_WEST,
  DEFAULT_SUN_ALTITUDE_DEC21_NOON,
  DEFAULT_FIXED_TILT,
  DEFAULT_EAST_WEST_TILT,
  DEFAULT_PAIR_GAP,
  DEFAULT_INTER_GAP,
  MOUNTING_BUCKET,
  MOUNTING_MODE,
  PANEL_THICKNESS_M,
  rackModeToMountingMode,
} from "@/constants.js";
import RemoveSolarGroupModal from "@/components/RightSidebar/RemoveSolarGroupModal.vue";

export default {
  name: "SolarSidebar",
  components: {
    PanelSelection,
    RestrictedArea,
    IndividualPlacement,
    GridInputGroup,
    GridAlignmentIcon,
    MountingTypeIcon,
    rowsSpacingIcon,
    columnsSpacingIcon,
    marginIcon,
    offsetIcon,
    RemoveSolarGroupModal,
  },
  props: [
    "selectedSolarGroup",
    "project",
    "areas",
    "horizontalPanels",
    "verticalPanels",
    "activeUserId",
    "populateArea",
    "createUpdateSolarGroup",
    "removeSolarGroupPanels",
    "addOffsetToPanels",
    "removeSolarGroup",
    "recenterCameraOnArea",
    "anonymousUser",
    "likedPanel",
    "starClicked",
    "unlikePanel",
    "likedPanelId",
    "disableRestrictedAreaMode",
    "loadCustomPanelTextures",
    "getUserDefaultSolarValues",
    "loadCustomPanelTextures",
    "getUserDefaultSolarValues",
    "active",
    "individualPanelOrientation",
    "enableIndividualPlacementMode",
    "disableIndividualPlacementMode",
    "validateIndividualPanels",
    "changePanelTypeForIndividualPanels",
    "solarGroupIndex",
  ],
  emits: [
    "setupCompleted",
    "solarGroupDeleted",
    "defaultValuesUpdates",
    "restrictedAreasPlacement",
    "toggleHotkeys",
  ],
  data() {
    const g  = this.selectedSolarGroup;
    const mc = g.mountingConfigurations ?? {};
    const ft = mc.fixed_tilt ?? {};
    const ew = mc.east_west  ?? {};
    const fl = mc.flush      ?? {};
    return {
      panelSelectionOpen: false,
      removeGroupModalOpen: false,
      recenterFlashing: false,
      icons: { tiltAngle: tiltAngleIcon, azimuth: azimuthIcon, interRowSpacing: interRowSpacingIcon, interRow: interRowIcon, pairGap: pairGapIcon, a2a: a2aIcon, reset: resetIcon },

      // Area-level fields.
      // Orientation lives per-mode now; mirror the active bucket's value
      // (falls back to the per-mode default when a tilted bucket is still
      // partial — only happens in-session before the user has saved).
      orientation: ((g.mountingConfigurations?.[MOUNTING_BUCKET[g.activeMountingMode]]?.orientation
        ?? (g.activeMountingMode === "FLUSH" ? "vertical" : "horizontal")) === "vertical"),
      margin:      String(g.margin  ?? 0),
      offset:      String(g.offset  ?? 0),

      // Active mode.
      rackMode: g.rackMode ?? null,

      // Flush bucket fields.
      horizontalSpacing: String(fl.horizontal_panel_spacing ?? 5),
      verticalSpacing:   String(fl.vertical_panel_spacing   ?? 5),
      rowOverlap:        fl.row_overlap ?? 0,

      // Fixed-tilt bucket fields (display; null = formula mode).
      // Tilt + azimuth are displayed in the same inputs for both tilted modes,
      // so seed them from whichever bucket is currently active. Reading from
      // `ft` unconditionally would surface 0/default values for an E-W area
      // until the user touched the input.
      tilt:            ((g.activeMountingMode === "EAST_WEST") ? (ew.tilt ?? DEFAULT_FIXED_TILT) : (ft.tilt ?? DEFAULT_FIXED_TILT)),
      azimuth:         ((g.activeMountingMode === "EAST_WEST") ? (ew.azimuth ?? 0) : (ft.azimuth ?? 0)),
      interRowSpacing: ft.inter_row_spacing ?? null,

      // East-West bucket fields.
      pairGap:    ew.pair_gap    ?? DEFAULT_PAIR_GAP,
      apexToApex: ew.apex_to_apex ?? null,
      // Persisted user clearance above the formula A2A minimum. Stored on the
      // backend so we don't have to derive it from (apex_to_apex - formula_min)
      // every fetch — that derivation drifts as tilt / pair_gap change.
      interGap:   ew.inter_gap ?? DEFAULT_INTER_GAP,

      // Internal transient flags.
      _suppressOverride:     false,   // prevent watcher mis-flagging formula writes
      _suppressAzimuthSync:  false,   // prevent double-populate on rotation drag
      _suppressInterGapSync: false,   // prevent A2A ⇄ interGap watcher loop
      _suppressTiltSync:     false,   // prevent double-populate on mode-switch tilt seed
      _suppressOrientationSync: false, // prevent double-populate on mode-switch orientation seed
      _suppressSelectionSync:   false, // set while the selectedSolarGroup watcher is mirroring the newly selected area's fields onto local state — every field watcher must early-return so the area isn't repopulated just for being selected
    };
  },
  computed: {
    ...mapGetters(["customPanels"]),
    featureFlag() {
      return (name) => this.$store.getters["featureFlag"](name);
    },
    activeBucketAzimuth() {
      const bucket = MOUNTING_BUCKET[this.selectedSolarGroup?.activeMountingMode];
      return this.selectedSolarGroup?.mountingConfigurations?.[bucket]?.azimuth;
    },
    activeFixedTilt() {
      return this.selectedSolarGroup?.mountingConfigurations?.fixed_tilt ?? null;
    },
    activeEastWest() {
      return this.selectedSolarGroup?.mountingConfigurations?.east_west ?? null;
    },
    activeFlush() {
      return this.selectedSolarGroup?.mountingConfigurations?.flush ?? null;
    },
    mountingModes() {
      return [
        { value: null,         iconMode: "flush",      labelKey: "rackMode.flush"     },
        { value: "EAST_WEST",  iconMode: "east-west",  labelKey: "rackMode.eastWest"  },
        { value: "FIXED_TILT", iconMode: "fixed-tilt", labelKey: "rackMode.fixedTilt" },
      ];
    },
    apexToApexMin() {
      // Mirrors `apexToApexFormulaMin` in eastWest.js — must stay in sync so
      // the UI's :min clamp and inter-gap baseline match the placement code.
      //   2·footH      + pair_gap (cm)         + t·sin·100        (cm)
      // = footprint     + apex gap (horizontal) + thickness creep across row
      const tiltRad = (this.tilt * Math.PI) / 180;
      const H       = this.panelDetails?.size.height ?? 0;
      const footH   = H * Math.cos(tiltRad);
      const minM    = 2 * footH + (this.pairGap ?? 5) / 100 + PANEL_THICKNESS_M * Math.sin(tiltRad);
      return parseFloat((minM * 100).toFixed(1));
    },
    // The A2A input is bound to this computed so the displayed value falls
    // back to the formula minimum when the bucket stores `null` (the "formula
    // mode" sentinel). Without this, el-input-number renders the null as 0 on
    // fetch even though the placement algorithm correctly uses the formula
    // minimum. Setter writes the literal value the user typed — stored as a
    // real number from then on, which is fine because subsequent tilt/pair-gap
    // changes go through `syncApexFromInterGap` and keep the value in sync.
    apexToApexDisplay: {
      get() { return this.apexToApex ?? this.apexToApexMin; },
      set(v) { this.apexToApex = v; },
    },
    // Formula-computed inter-row spacing (cm) for the current panel + tilt.
    // Mirrors `computeInterRowSpacing` in fixedTilt.js:
    //   gap (m) = panelHeight · sin(tilt) / tan(sun_altitude)
    // Multiplied by 100 for the cm-based input.
    interRowSpacingFormula() {
      if (!this.panelDetails) return 0;
      const H    = this.panelDetails.size.height;
      const gapM = computeInterRowSpacing(H, parseFloat(this.tilt) || 0, DEFAULT_SUN_ALTITUDE_DEC21_NOON);
      return parseFloat((gapM * 100).toFixed(1));
    },
    // The inter-row spacing input is bound to this — same pattern as
    // apexToApexDisplay: null in the bucket means "formula mode", and we
    // show the formula's current value rather than leaving the field blank.
    // Setter writes through to this.interRowSpacing, which the existing
    // watcher persists to the bucket.
    interRowSpacingDisplay: {
      get() { return this.interRowSpacing ?? this.interRowSpacingFormula; },
      set(v) { this.interRowSpacing = v; },
    },
    // Minimum pair gap (cm) at which the two tilted panels' BACK apex edges
    // touch at the ridge line:
    //   thickness * sin(tilt)
    // Each panel's back-top edge sits (t/2)·sin(tilt) closer to the ridge than
    // its center plane, because `faceDir` tilts the front outward. Two mirror
    // panels contribute (t/2)·sin(tilt) each → total inward creep is t·sin(tilt),
    // so pair_gap = t·sin(tilt) puts both back edges exactly at the ridge.
    // Falls to 0 at tilt = 0 (flat panels can touch face-to-face) and grows
    // linearly with tilt.
    pairGapMin() {
      const tiltRad = (parseFloat(this.tilt) || 0) * Math.PI / 180;
      const minCm   = (PANEL_THICKNESS_M * 100) * Math.sin(tiltRad);
      return parseFloat(minCm.toFixed(1));
    },
    isBobbieContext() {
      return this.$store.state.auth?.isBobbieContext || false;
    },
    solarGroupIndex() {
      return this.areas.findIndex((area) => area === this.selectedSolarGroup) + 1;
    },
    displayTitle() {
      const customName = this.selectedSolarGroup.name?.trim();
      return customName || `${this.$t("panelMenu.pvArray")} ${this.solarGroupIndex}`;
    },
    panelDetails() {
      const list = this.orientation ? this.verticalPanels : this.horizontalPanels;
      let panel = list.find((p) => p.id === this.selectedSolarGroup.panelId);
      if (!panel) {
        const targetOrientation = this.orientation ? "vertical" : "horizontal";
        const customs = this.customPanels || [];
        panel = customs.find(
          (p) => p.id === this.selectedSolarGroup.panelId && p.orientation === targetOrientation
        );
        if (!panel) {
          panel = customs.find((p) => p.id === this.selectedSolarGroup.panelId);
        }
      }
      if (!panel) panel = list[0];
      return panel;
    },
    greaterPanelDimension() {
      return Math.max(this.panelDetails.size.width, this.panelDetails.size.height);
    },
    lowerPanelDimension() {
      return Math.min(this.panelDetails.size.width, this.panelDetails.size.height);
    },
    textureUrl() {
      if (!this.panelDetails.textureId) return "/assets/textures/texture-black.svg";
      if (this.panelDetails.textureId == "95") return "/assets/textures/texture-red.png";
      return `/assets/textures/solar_panel_texture_half_cell_${this.panelDetails.textureId}_horizontal.png`;
    },
    placementReady() {
      if (
        !this.isValidMargin ||
        !this.isValidOffset ||
        !this.isValidHorizontalSpacing ||
        !this.isValidVerticalSpacing ||
        !this.selectedSolarGroup.panelId
      )
        return false;
      return true;
    },
    isValidMargin() {
      return (
        this.margin !== null &&
        !isNaN(this.margin) &&
        this.margin > -1 &&
        this.margin < 100
      );
    },
    isValidOffset() {
      return (
        this.offset !== null &&
        !isNaN(this.offset) &&
        this.offset > -1 &&
        this.offset < 100
      );
    },
    isValidHorizontalSpacing() {
      return (
        this.horizontalSpacing !== null &&
        !isNaN(this.horizontalSpacing) &&
        this.horizontalSpacing > -1 &&
        this.horizontalSpacing < 100
      );
    },
    isValidVerticalSpacing() {
      return (
        this.verticalSpacing !== null &&
        !isNaN(this.verticalSpacing) &&
        this.verticalSpacing > -1 &&
        this.verticalSpacing < 100
      );
    },
    individualPanelsExist() {
      let individualPanelsCount = 0;
      if (this.selectedSolarGroup.verticalPanelInstancedMesh)
        individualPanelsCount += this.selectedSolarGroup.verticalPanelInstancedMesh
          .instancesCount;
      if (this.selectedSolarGroup.horizontalPanelInstancedMesh)
        individualPanelsCount += this.selectedSolarGroup.horizontalPanelInstancedMesh
          .instancesCount;

      return individualPanelsCount > 0;
    },
    getDeleteText() {
      return this.$i18n.locale === "de"
        ? `${this.displayTitle} ${this.$t("panelMenu.deleteSolarGroup")}`
        : `${this.$t("panelMenu.deleteSolarGroup")} ${this.displayTitle}`;
    },
  },
  watch: {
    margin() {
      if (this._suppressSelectionSync) return;
      if (this.margin === null) return;
      this.handleCompleteUpdateSolarGroup();
    },
    offset() {
      if (this._suppressSelectionSync) return;
      if (this.offset === null) return;
      this.handleCompleteUpdateSolarGroup();
    },
    verticalSpacing() {
      if (this._suppressSelectionSync) return;
      if (this.verticalSpacing === null) return;
      this.handleCompleteUpdateSolarGroup();
    },
    horizontalSpacing() {
      if (this._suppressSelectionSync) return;
      if (this.horizontalSpacing === null) return;
      this.handleCompleteUpdateSolarGroup();
    },
    orientation() {
      if (this._suppressSelectionSync) return;
      if (this._suppressOrientationSync) return;
      // Don't pre-write the bucket here — `handleCompleteUpdateSolarGroup`
      // does the bucket write itself, and `nothingChanged()` compares
      // `this.orientation` against the bucket's stored orientation. If we
      // updated the bucket first, the diff would be a no-op and panels
      // wouldn't replace.
      if (this.rackMode === FIXED_TILT && this.activeFixedTilt?.inter_row_spacing == null) {
        this.recomputeFormulaSpacing();
      }
      if (this.rackMode === EAST_WEST) {
        this.syncApexFromInterGap();
      }
      this.handleCompleteUpdateSolarGroup();
    },
    rowOverlap() {
      if (this._suppressSelectionSync) return;
      clearTimeout(this._rowOverlapTimer);
      this._rowOverlapTimer = setTimeout(() => {
        this.handleCompleteUpdateSolarGroup();
      }, 500);
    },
    rackMode(val) {
      // Skip the repopulate (and the area mutation, which is a no-op anyway)
      // when this fired because the user picked a different solar group, not
      // because they toggled the mounting type.
      if (this._suppressSelectionSync) return;

      this.selectedSolarGroup.rackMode = val;
      this.selectedSolarGroup.activeMountingMode = rackModeToMountingMode(val);

      this.restoreSidebarFromBucket(val);

      if (val === FIXED_TILT) {
        // Seed azimuth from project orientation only if the bucket genuinely
        // has no stored value. `!= null` is critical — `!azimuth` would also
        // be true for a legitimately stored value of 0 and re-seed it to 180.
        if (this.selectedSolarGroup.mountingConfigurations.fixed_tilt?.azimuth == null) {
          const az = computeDefaultAzimuth(this.selectedSolarGroup, this.project);
          if (!this.selectedSolarGroup.mountingConfigurations.fixed_tilt)
            this.selectedSolarGroup.mountingConfigurations.fixed_tilt = {};
          this.selectedSolarGroup.mountingConfigurations.fixed_tilt.azimuth = az;
          this.azimuth = az;
        }
        // Seed inter_row_spacing display if in formula mode.
        if (this.selectedSolarGroup.mountingConfigurations.fixed_tilt?.inter_row_spacing == null) {
          this.recomputeFormulaSpacing();
        }
      }
      if (val === EAST_WEST) {
        if (this.selectedSolarGroup.mountingConfigurations.east_west?.azimuth == null) {
          const az = computeDefaultAzimuth(this.selectedSolarGroup, this.project);
          if (!this.selectedSolarGroup.mountingConfigurations.east_west)
            this.selectedSolarGroup.mountingConfigurations.east_west = {};
          this.selectedSolarGroup.mountingConfigurations.east_west.azimuth = az;
          this.azimuth = az;
        }
        this.syncApexFromInterGap();
      }
      this.removeSolarGroupPanels(this.selectedSolarGroup);
      this.populateArea(this.selectedSolarGroup);
    },
    tilt() {
      if (this._suppressSelectionSync) return;
      if (this._suppressTiltSync) return;
      if (this.rackMode === FIXED_TILT) {
        if (this.activeFixedTilt?.inter_row_spacing == null) this.recomputeFormulaSpacing();
        this.handleTiltedUpdate();
      }
      if (this.rackMode === EAST_WEST) {
        this.syncApexFromInterGap();
        this.handleTiltedUpdate();
      }
    },
    azimuth() {
      if (this._suppressSelectionSync) return;
      if (this._suppressAzimuthSync) return;
      this.handleTiltedUpdate();
    },
    activeBucketAzimuth(newVal) {
      // Rotation drag writes to the bucket directly — mirror into local input.
      if (newVal == null || newVal === this.azimuth) return;
      this._suppressAzimuthSync = true;
      this.azimuth = newVal;
      this.$nextTick(() => { this._suppressAzimuthSync = false; });
    },
    pairGap() {
      if (this._suppressSelectionSync) return;
      if (this.rackMode === EAST_WEST) this.syncApexFromInterGap();
      this.handleTiltedUpdate();
    },
    interGap() {
      if (this._suppressSelectionSync) return;
      if (this._suppressInterGapSync) return;
      this.syncApexFromInterGap();
      this.handleTiltedUpdate();
    },
    interRowSpacing() {
      if (this._suppressSelectionSync) return;
      // Formula writes are guarded by _suppressOverride — those don't reach the bucket.
      if (this._suppressOverride) return;
      // User edit: store value in bucket (null = reset to formula).
      if (this.selectedSolarGroup?.mountingConfigurations?.fixed_tilt != null) {
        this.selectedSolarGroup.mountingConfigurations.fixed_tilt.inter_row_spacing = this.interRowSpacing;
      }
      this.handleTiltedUpdate();
    },
    apexToApex() {
      if (this._suppressSelectionSync) return;
      if (this._suppressInterGapSync) return;
      if (this.selectedSolarGroup?.mountingConfigurations?.east_west != null) {
        this.selectedSolarGroup.mountingConfigurations.east_west.apex_to_apex = this.apexToApex;
      }
      this.syncInterGapFromApex();
      this.handleTiltedUpdate();
    },
    selectedSolarGroup(g) {
      if (!g) return;
      // Mirroring the new area's fields onto local state would otherwise fire
      // every field watcher (margin / tilt / azimuth / spacing / …), each of
      // which triggers a repopulate. The area is already correctly drawn, so
      // every one of those watchers must early-return for the duration of
      // this hydration.
      this._suppressSelectionSync = true;

      const mc = g.mountingConfigurations ?? {};
      const ft = mc.fixed_tilt ?? {};
      const ew = mc.east_west  ?? {};
      const fl = mc.flush      ?? {};
      const activeBucket    = mc[MOUNTING_BUCKET[g.activeMountingMode]] ?? {};
      const defaultOrient   = g.activeMountingMode === "FLUSH" ? "vertical" : "horizontal";
      this.orientation      = (activeBucket.orientation ?? defaultOrient) === "vertical";
      this.margin           = String(g.margin  ?? 0);
      this.offset           = String(g.offset  ?? 0);
      this.rackMode         = g.rackMode ?? null;
      this.horizontalSpacing = String(fl.horizontal_panel_spacing ?? 5);
      this.verticalSpacing   = String(fl.vertical_panel_spacing   ?? 5);
      this.rowOverlap        = fl.row_overlap ?? 0;
      // Same active-bucket logic as data() — show stored tilt/azimuth for whichever
      // tilted mode the area was last saved in, not always fixed_tilt.
      const isEW = g.activeMountingMode === "EAST_WEST";
      this.tilt              = isEW ? (ew.tilt ?? DEFAULT_FIXED_TILT) : (ft.tilt ?? DEFAULT_FIXED_TILT);
      this.azimuth           = isEW ? (ew.azimuth ?? 0)              : (ft.azimuth ?? 0);
      this.interRowSpacing   = ft.inter_row_spacing ?? null;
      this.pairGap           = ew.pair_gap    ?? DEFAULT_PAIR_GAP;
      this.apexToApex        = ew.apex_to_apex ?? null;
      this.interGap          = ew.inter_gap   ?? DEFAULT_INTER_GAP;

      this.$nextTick(() => { this._suppressSelectionSync = false; });
    },
  },
  mounted() {
    const rsb = document.getElementById("right-side-bar");
    rsb.style.height = "100vh";
  },
  beforeUnmount() {
    const rsb = document.getElementById("right-side-bar");
    rsb.style.height = "auto";
    clearTimeout(this._rowOverlapTimer);
  },
  methods: {
    handlePanelSelectionPopup(open) {
      this.panelSelectionOpen = open;
    },
    // Save user defaults for the active mode. Sends area-level fields plus
    // the bucket that matches the current mounting mode — so editing margin/
    // offset/orientation in fixed-tilt persists the fixed-tilt bucket as the
    // default, not flush.
    // Live-update the azimuth input during rotation drag without touching the
    // mounting bucket. Writing to the bucket per frame fans out through Vue
    // computeds/watchers and tanks framerate; this just nudges the local input
    // value with the sync-suppression flag set so the azimuth watcher's
    // populate path stays dormant. Final commit to the bucket happens at
    // drag-end (see area.js `onRotateSolarGroupEnd`).
    setAzimuthDisplay(newAz) {
      this._suppressAzimuthSync = true;
      this.azimuth = newAz;
      this.$nextTick(() => { this._suppressAzimuthSync = false; });
    },
    persistActiveModeDefaults() {
      if (this.sample) return;
      // FRONTEND_ONLY_DEMO (sample routes) has no Update_Objects — skip the
      // network call rather than letting it round-trip just to 401.
      if (!hasPermission(Permission.Update_Objects)) return;
      const area      = this.selectedSolarGroup;
      const bucketKey = MOUNTING_BUCKET[area.activeMountingMode];
      API.airteam3DViewer
        .updateDefaultValues({
          panel_id:    area.panelId,
          margin:      area.margin,
          offset:      area.offset,
          [bucketKey]: area.mountingConfigurations?.[bucketKey] ?? null,
        })
        .then((response) => {
          this.$emit("defaultValuesUpdates", response.data);
        });
    },
    async handleCompleteUpdateSolarGroup(panelSelected = false) {
      if (!this.placementReady) return;
      const area     = this.selectedSolarGroup;
      const newOffset = parseFloat(this.offset);
      const onlyOffsetChanged = this.onlyOffsetChanged();
      const offsetDifference  = newOffset - area.offset;

      // Decide what to do BEFORE mutating area/bucket — `nothingChanged()`
      // compares the local sidebar values against the area/bucket as the
      // source of truth. If we wrote first, every call would look like a
      // no-op and the populate path would never run.
      if (onlyOffsetChanged) {
        this.addOffsetToPanels(area, offsetDifference);
      } else if (this.nothingChanged() && !panelSelected) {
        return;
      } else {
        this.removeSolarGroupPanels(area);
      }

      // Commit changes onto the area + active bucket.
      area.orientation = this.orientation;
      area.margin      = parseFloat(this.margin);
      area.offset      = newOffset;

      const activeBucketKey = MOUNTING_BUCKET[area.activeMountingMode];
      if (area.mountingConfigurations?.[activeBucketKey]) {
        area.mountingConfigurations[activeBucketKey].orientation =
          this.orientation ? "vertical" : "horizontal";
      }

      // Flush-bucket spacing/overlap fields are only visible/editable in
      // flush mode — touch them only when we're actually in flush, otherwise
      // we'd overwrite stored flush values with stale local data when the
      // user edits a different field in a tilted mode.
      if (area.rackMode === null) {
        area.mountingConfigurations.flush = {
          ...(area.mountingConfigurations.flush ?? {}),
          horizontal_panel_spacing: parseFloat(this.horizontalSpacing),
          vertical_panel_spacing:   parseFloat(this.verticalSpacing),
          row_overlap:              this.rowOverlap ?? 0,
        };
      }

      this.persistActiveModeDefaults();

      if (!onlyOffsetChanged) {
        this.populateArea(area);
        this.validateIndividualPanels(area);
      } else {
        this.createUpdateSolarGroup(area);
      }
    },
    nothingChanged() {
      const area    = this.selectedSolarGroup;
      const fl      = area.mountingConfigurations?.flush ?? {};
      const bucket  = area.mountingConfigurations?.[MOUNTING_BUCKET[area.activeMountingMode]] ?? {};
      const bucketOrientation = (bucket.orientation ?? "vertical") === "vertical";
      return (
        area.offset === parseFloat(this.offset)       &&
        area.margin === parseFloat(this.margin)       &&
        bucketOrientation === this.orientation        &&
        (fl.vertical_panel_spacing   ?? 0)  === parseFloat(this.verticalSpacing)   &&
        (fl.horizontal_panel_spacing ?? 0)  === parseFloat(this.horizontalSpacing) &&
        (fl.row_overlap ?? 0)               === this.rowOverlap
      );
    },
    onlyOffsetChanged() {
      const area    = this.selectedSolarGroup;
      const fl      = area.mountingConfigurations?.flush ?? {};
      const bucket  = area.mountingConfigurations?.[MOUNTING_BUCKET[area.activeMountingMode]] ?? {};
      const bucketOrientation = (bucket.orientation ?? "vertical") === "vertical";
      return (
        area.offset !== parseFloat(this.offset)      &&
        area.margin === parseFloat(this.margin)      &&
        bucketOrientation === this.orientation       &&
        (fl.vertical_panel_spacing   ?? 0)  === parseFloat(this.verticalSpacing)  &&
        (fl.horizontal_panel_spacing ?? 0)  === parseFloat(this.horizontalSpacing)
      );
    },
    async handleDeleteSolarGroup() {
      await this.removeSolarGroup(this.selectedSolarGroup);
      this.$emit("solarGroupDeleted");
    },
    async handleChangeSelectPanel(panel) {
      this.selectedSolarGroup.panelId = panel.id;
      this.panelSelectionOpen = false;
      this.changePanelTypeForIndividualPanels(this.selectedSolarGroup);
      this.validateIndividualPanels(this.selectedSolarGroup);
      this.handleCompleteUpdateSolarGroup(true);
    },
    onRecenterClick() {
      this.recenterCameraOnArea(this.selectedSolarGroup);
      this.recenterFlashing = true;
      setTimeout(() => {
        this.recenterFlashing = false;
      }, 500);
    },
    closeSidebar() {
      if (!this.placementReady) {
        const fl = this.selectedSolarGroup.mountingConfigurations?.flush ?? {};
        this.verticalSpacing   = String(fl.vertical_panel_spacing   ?? 5);
        this.horizontalSpacing = String(fl.horizontal_panel_spacing ?? 5);
        this.rowOverlap        = fl.row_overlap ?? 0;
        this.margin            = String(this.selectedSolarGroup.margin ?? 0);
        this.offset            = String(this.selectedSolarGroup.offset ?? 0);
        const area     = this.selectedSolarGroup;
        const bucket   = area.mountingConfigurations?.[MOUNTING_BUCKET[area.activeMountingMode]] ?? {};
        const fallback = area.activeMountingMode === "FLUSH" ? "vertical" : "horizontal";
        this.orientation = (bucket.orientation ?? fallback) === "vertical";
      }
      this.disableRestrictedAreaMode();
      this.disableIndividualPlacementMode();
      this.$emit("setupCompleted");
    },
    handleCreateRestrictedArea(e) {
      this.$emit("restrictedAreasPlacement", e);
    },
    closeRemoveGroupModal() {
      this.removeGroupModalOpen = false;
    },
    // Restore sidebar local fields from the incoming mode's bucket.
    // Buckets are always current so no snapshot step is needed.
    restoreSidebarFromBucket(newRackMode) {
      const area = this.selectedSolarGroup;
      const mc   = area?.mountingConfigurations ?? {};
      let bucketOrientation;
      if (newRackMode === null) {
        const fl = mc.flush ?? {};
        this.verticalSpacing   = String(fl.vertical_panel_spacing   ?? 5);
        this.horizontalSpacing = String(fl.horizontal_panel_spacing ?? 5);
        this.rowOverlap        = fl.row_overlap ?? 0;
        bucketOrientation      = fl.orientation ?? "vertical";
      } else if (newRackMode === FIXED_TILT) {
        const ft = mc.fixed_tilt ?? {};
        this.tilt            = ft.tilt    ?? DEFAULT_FIXED_TILT;
        this.azimuth         = ft.azimuth ?? 0;
        this.interRowSpacing = ft.inter_row_spacing ?? null;
        bucketOrientation    = ft.orientation ?? "horizontal";
        if (ft.inter_row_spacing == null) this.recomputeFormulaSpacing();
      } else if (newRackMode === EAST_WEST) {
        const ew = mc.east_west ?? {};
        this.tilt       = ew.tilt        ?? DEFAULT_EAST_WEST_TILT;
        this.azimuth    = ew.azimuth     ?? 0;
        this.apexToApex = ew.apex_to_apex ?? null;
        this.pairGap    = ew.pair_gap    ?? DEFAULT_PAIR_GAP;
        this.interGap   = ew.inter_gap   ?? DEFAULT_INTER_GAP;
        bucketOrientation = ew.orientation ?? "horizontal";
        this.syncApexFromInterGap();
      }
      // Mirror the new mode's orientation onto local + area without firing the
      // orientation watcher's full populate (the rackMode handler triggers its
      // own populate next, so we just suppress the duplicate path).
      const desired = bucketOrientation === "vertical";
      if (this.orientation !== desired) {
        this._suppressOrientationSync = true;
        this.orientation = desired;
        area.orientation = desired;
        this.$nextTick(() => { this._suppressOrientationSync = false; });
      }
    },
    handleTiltedUpdate() {
      const area = this.selectedSolarGroup;
      const mc   = area.mountingConfigurations;
      const mode = area.activeMountingMode;

      const orientation = this.orientation ? "vertical" : "horizontal";
      if (mode === MOUNTING_MODE.FIXED_TILT) {
        mc.fixed_tilt = {
          ...(mc.fixed_tilt ?? {}),
          orientation,
          tilt:              parseFloat(this.tilt) || 0,
          azimuth:           parseFloat(this.azimuth) || 0,
          // inter_row_spacing: null = formula mode (display value handled locally).
          // Watcher only writes here when user edits; reset sets back to null.
        };
      } else if (mode === MOUNTING_MODE.EAST_WEST) {
        // Clamp pair_gap to its tilt-dependent minimum (2 * thickness * sin(tilt))
        // so a tilt increase can't leave the bucket with a sub-minimum value
        // until the user happens to touch the field.
        const clampedPairGap = Math.max(this.pairGap ?? DEFAULT_PAIR_GAP, this.pairGapMin);
        if (clampedPairGap !== this.pairGap) this.pairGap = clampedPairGap;
        mc.east_west = {
          ...(mc.east_west ?? {}),
          orientation,
          tilt:        parseFloat(this.tilt) || 0,
          azimuth:     parseFloat(this.azimuth) || 0,
          apex_to_apex: this.apexToApex,
          inter_gap:   parseFloat(this.interGap) || 0,
          pair_gap:    clampedPairGap,
        };
      }
      this.removeSolarGroupPanels(area);
      this.populateArea(area);
      this.persistActiveModeDefaults();
    },
    syncApexFromInterGap() {
      const gap  = parseFloat(this.interGap) || 0;
      const next = parseFloat((this.apexToApexMin + gap).toFixed(1));
      if (this.apexToApex === next) return;
      this._suppressInterGapSync = true;
      this.apexToApex = next;
      if (this.selectedSolarGroup?.mountingConfigurations?.east_west) {
        this.selectedSolarGroup.mountingConfigurations.east_west.apex_to_apex = next;
      }
      this.$nextTick(() => { this._suppressInterGapSync = false; });
    },
    syncInterGapFromApex() {
      const current = parseFloat(this.apexToApex) || this.apexToApexMin;
      const next    = parseFloat(Math.max(0, current - this.apexToApexMin).toFixed(1));
      if (this.interGap === next) return;
      this._suppressInterGapSync = true;
      this.interGap = next;
      this.$nextTick(() => { this._suppressInterGapSync = false; });
    },
    // Compute formula spacing for DISPLAY only. Bucket stays null (formula mode).
    // _suppressOverride prevents the watcher from writing the computed value back.
    recomputeFormulaSpacing() {
      if (!this.panelDetails) return;
      const H      = this.panelDetails.size.height;
      const gapM   = computeInterRowSpacing(H, parseFloat(this.tilt) || 0, DEFAULT_SUN_ALTITUDE_DEC21_NOON);
      const gapCm  = parseFloat((gapM * 100).toFixed(1));
      this._suppressOverride = true;
      this.interRowSpacing   = gapCm;
      this.$nextTick(() => { this._suppressOverride = false; });
    },
    resetAzimuth() {
      const az   = computeDefaultAzimuth(this.selectedSolarGroup, this.project);
      const bucket = MOUNTING_BUCKET[this.selectedSolarGroup.activeMountingMode];
      this.azimuth = az;
      if (this.selectedSolarGroup.mountingConfigurations?.[bucket]) {
        this.selectedSolarGroup.mountingConfigurations[bucket].azimuth = az;
      }
      this.handleTiltedUpdate();
    },
    resetInterRowSpacing() {
      // Set bucket to null = formula mode, update display.
      if (this.selectedSolarGroup?.mountingConfigurations?.fixed_tilt) {
        this.selectedSolarGroup.mountingConfigurations.fixed_tilt.inter_row_spacing = null;
      }
      this.recomputeFormulaSpacing();
      this.handleTiltedUpdate();
    },
    resetApexToApex() {
      this._suppressInterGapSync = true;
      this.interGap   = 0;
      this.apexToApex = this.apexToApexMin;
      if (this.selectedSolarGroup?.mountingConfigurations?.east_west) {
        this.selectedSolarGroup.mountingConfigurations.east_west.apex_to_apex = this.apexToApexMin;
      }
      this.$nextTick(() => { this._suppressInterGapSync = false; });
      this.handleTiltedUpdate();
    },
  },
};
</script>

<style lang="scss" scoped>
.inter-gap-row {
  margin-left: 12px;
  padding-left: 12px;
  border-left: 1px solid var(--color-dark-bg-quaternary);
}

.main-container {
  background-color: var(--color-dark-bg-primary);
  padding: 10px;
  height: 100%;
  overflow: auto;
  width: 270px;

  @media (max-width: 560px) {
    width: 200px;
  }
}

.child-container {
  padding: 10px 5px;
  border-radius: 5px;
  background-color: var(--color-dark-bg-secondary);
  margin-bottom: 8px;
  border: 1px solid var(--color-border-dark);
  text-align: left;
  color: white;

  &--last {
    margin-bottom: 12px;
  }

  &--remove {
    text-align: left;
    margin-bottom: 64px;
  }
}

.divider {
  margin: 15px 0;
  height: 1px;
  border-radius: 2px;
  background-color: var(--color-dark-bg-quaternary);
}

.panel-img {
  width: 30px;
  margin-right: 8px;
  z-index: 2;
}

.title {
  color: white;
  font-weight: 600;
  font-size: 16px;

  &--panel {
    font-weight: 400;
  }

  &--red {
    color: var(--color-error-red);
  }

  &--main {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &--lg {
      font-size: 18px;
    }
  }
}

.header-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  margin-right: 6px;
}

.recenter-btn {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.recenter-icon {
  display: block;
}

.recenter-corners path {
  fill: white;
  transition: fill 150ms ease-in-out;
}

.recenter-dot {
  fill: white;
}

.recenter-btn.is-flashing .recenter-corners path {
  fill: #23e7a5;
}

.selected-panel {
  background-color: var(--color-dark-bg-primary);
  padding: 10px;
  border-radius: 5px;
  border: 0;
}

.select-panel-btn {
  color: var(--color-gree300);
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
}

.mounting-type-selector {
  display: flex;
  background-color: #2b2e30;
  height: 59px;
  align-items: center;
}

.mounting-mode {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 41px;
  gap: 6px;
  overflow: hidden;
  cursor: pointer;

  &__icon {
    height: 17px;
    width: auto;
    opacity: 0.6;
  }

  &__label {
    font-size: 11px;
    color: white;
    white-space: nowrap;
    letter-spacing: 0.2px;
  }

  &--active {
    .mounting-mode__icon {
      opacity: 1;
      filter: invert(72%) sepia(53%) saturate(413%) hue-rotate(112deg) brightness(95%) contrast(89%);
    }
    .mounting-mode__label {
      color: var(--color-gree300);
    }
  }
}

.tilted-input {
  width: 130px;
  color: white;

  :deep(.el-input__wrapper) {
    position: relative;
    display: inline-block;
    box-shadow: none;
    background-color: var(--color-dark-bg-primary);
    padding-left: 8px;
    padding-right: 74px;

    &::after {
      content: var(--unit-label, "cm");
      position: absolute;
      top: 50%;
      right: var(--unit-right, 64px);
      transform: translateY(-50%);
      font-size: 14px;
      color: white;
      pointer-events: none;
    }
  }

  :deep(.el-input__inner) {
    color: white;
    text-align: center;
  }

  :deep(.el-input-number__decrease),
  :deep(.el-input-number__increase) {
    box-shadow: none;
    background-color: var(--color-dark-bg-primary);
    border-color: var(--color-dark-bg-secondary) !important;
    color: white;
  }
}


.reset-btn {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.alignment-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}

.custom-alignment-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 168px;
  margin-top: 10px;
}

.custom-alignment-input {
  width: 130px;
  color: white;

  :deep(.el-input__wrapper) {
    position: relative;
    display: inline-block;
    box-shadow: none;
    background-color: var(--color-dark-bg-primary);
    padding-left: 8px;
    padding-right: 74px;

    &::after {
      content: "%";
      position: absolute;
      top: 50%;
      right: 64px;
      transform: translateY(-50%);
      font-size: 14px;
      color: white;
      pointer-events: none;
    }
  }

  :deep(.el-input__inner) {
    color: white;
    text-align: center;
    border-radius: 2px !important;
  }

  :deep(.el-input-number__decrease),
  :deep(.el-input-number__increase) {
    box-shadow: none;
    background-color: var(--color-dark-bg-primary);
    border-color: var(--color-dark-bg-secondary) !important;
    color: white;
  }
}

.alignment-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 4px;
  border-radius: 8px;
  border: 1px solid var(--color-border-dark);
  background-color: var(--color-dark-bg-primary);
  cursor: pointer;
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--color-gree300);
  }

  &--active {
    border-color: var(--color-gree300);
  }

  &__icon {
    width: 36px;
    height: auto;
  }

  &__label {
    font-size: 12px;
    color: white;
  }
}

</style>
